import { combine, createEffect, createEvent, createStore } from 'effector';
import { get } from 'lodash';
import request from '@helpers/request';
import api from '@constants/endpoints';
import createPreloadersStore from './preloaders';
import createPaginationStore from './pagination';
import createFiltersStore from './filters';
import update from 'immutability-helper';
import { ITribes } from '@interfaces/ITribes';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import { parseTribesFilters } from '@helpers/tribesFilters';

export const reset = createEvent('Tribes store reset');

const defaultFilters = parseTribesFilters({});

export const getList = createEffect({
  name: 'getList',
  async handler() {
    const { current, pageSize } = pagination.getState();

    const {
      sort,
      tribeName,
      url,
      createdFrom,
      createdTo,
      owner,
      member,
      game,
      hasClaims,
      isBlocked,
      membersMin,
      membersMax,
    } = filters.getState();

    const data = {
      filter: removeUndefinedFilters({
        name: tribeName,
        url,
        createdFrom,
        createdTo,
        owner,
        member,
        game,
        hasClaims,
        isBlocked,
        membersMin,
        membersMax,
      }),
      pagination: {
        type: 'PAGE',
        pageSize,
        page: (current || 1) - 1,
      },
      sort,
    };

    return request(api.tribes.getList, { data });
  },
});

export const deleteTribe = createEffect({
  name: 'deleteTribe',
  async handler(tribeId: ITribes['id']) {
    const response = await request(api.tribes.deleteTribe, {
      urlParams: { tribeId },
    });
    return response;
  },
});

export const blockTribe = createEffect({
  name: 'blockTribe',
  async handler(tribeId: ITribes['id']) {
    return request(api.tribes.blockTribe, { urlParams: { tribeId } });
  },
});

export const unblockTribe = createEffect({
  name: 'unblockTribe',
  async handler(tribeId: ITribes['id']) {
    return request(api.tribes.unblockTribe, { urlParams: { tribeId } });
  },
});

export const getClaimsByTribe = createEffect({
  name: 'getClaimsByTribe',
  async handler(tribeId: string) {
    return request(api.tribes.getClaimsByTribe, { urlParams: { tribeId }, data: {} });
  },
});

export const updateListHandler = (
  state: ITribes[],
  { result, params: { id } }: { params: { id?: string }; result: { data: ITribes } },
) => {
  if (id) {
    const foundIndex = state.findIndex((item: ITribes) => item.id === id);
    if (foundIndex >= 0) {
      const updatedState = update(state, { [foundIndex]: { $set: result.data } });
      getList();
      return updatedState;
    }
  } else {
    getList();
  }
};

export const deleteHandler = (state: ITribes[], { params }: { params: ITribes['id'] }) => {
  const itemIndex = state.findIndex((tribe: ITribes) => tribe.id === params);
  if (itemIndex >= 0) {
    return update(state, {
      $splice: [[itemIndex, 1]],
    });
  }
};

export const editTribe = createEffect({
  name: 'editTribe',
  async handler(data: ITribes) {
    const formdata = new FormData();
    formdata.append('about', data.about);
    const response = await request(api.tribes.editTribe, {
      urlParams: { tribeId: data.id },
      data: formdata,
    });
    return response;
  },
});

const preloaders = createPreloadersStore([getList, deleteTribe, blockTribe, unblockTribe]);

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'tribes',
  resetEvent: reset,
  bodyPath: ['data', 'body', 'result'],
});
export { setPagination };

const { filters, setFilters } = createFiltersStore({
  listName: 'tribes',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const list = createStore<ITribes[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'body', 'result', 'page'], []))
  .on(deleteTribe.done, deleteHandler)
  .on(editTribe.done, updateListHandler)
  .on(blockTribe.done, (state) => {
    getList();
    return state;
  })
  .on(unblockTribe.done, (state) => {
    getList();
    return state;
  })
  .reset(reset);

const tribes = combine({ list, preloaders, pagination, setPagination, filters, setFilters });

export default tribes;
